import type { Immersive, Installation, Media, Video } from '@/types';

/**
 * Which room does a catalogue entry have, and where does it open?
 *
 * A room belongs to its entry: it opens at the entry's own address with /room on the end, under
 * Installations or Immersive, wherever the entry lives. Two kinds exist. A template is a room
 * the site can build around any film (the entry's Room tab names the template and the film, and
 * the room takes its size from the film and the work's dimensions: a curved wall, or a hanging
 * LED ring). A hand-built room was written for one work and is
 * found by that work's slug. Plain data in, plain data out: servers and browsers both use this.
 */
export type RoomSection = 'installations' | 'immersive';
export type RoomTemplate = 'panoramicWall' | 'ledRing';
export type HandBuiltRoom = 'visions' | 'savingaesthetics' | 'tisa' | 'tisa2' | 'memememory' | 'relic' | 'easternpromises';

export type RoomSpec =
  | { kind: 'portal' }
  | { kind: 'handBuilt'; room: HandBuiltRoom }
  | { kind: 'template'; template: RoomTemplate; film: string; dimensions?: string | null };

export type RoomEntry = Pick<Installation | Immersive, 'slug' | 'title' | 'video'> & {
  room?: Installation['room'];
  /** The work's own size, as its entry words it; a template that has a size of its own reads it. */
  dimensions?: string | null;
};

/** The works with a room of their own, by catalogue slug. */
const HAND_BUILT: Record<string, HandBuiltRoom> = {
  visions: 'visions',
  savingaesthetics: 'savingaesthetics',
  tisa_in: 'tisa',
  tisa_2: 'tisa2',
  memememory: 'memememory',
  'relic-sculpture': 'relic',
  'eastern-promises': 'easternpromises',
};

export const TEMPLATES: RoomTemplate[] = ['panoramicWall', 'ledRing'];

// One address for both lists: the catalogue's Immersive section holds installations and immersive
// works together (the old /catalog/installations address redirects). The section still says which
// collection an entry is read from.
export const entryHref = (_section: RoomSection, slug: string) => `/catalog/immersive/${slug}`;
export const roomHref = (section: RoomSection, slug: string) => `${entryHref(section, slug)}/room`;

/**
 * A film's address as the room should ask for it. The catalogue gives a path on the site itself
 * (/api/video/file/...), which the site forwards, so the browser may sample the film in WebGL
 * with no cross-origin setup. It is used as given.
 */
function filmUrl(film: number | Video | Media | null | undefined): string | null {
  if (!film || typeof film === 'number') return null;
  return film.url || null;
}

/**
 * Whose catalogue the entry is in. The portal and the hand-built rooms are Orkhan's works, found by
 * his slugs: another client's entry with the same slug is a different work and gets no room of his.
 * Templates belong to every site. Leaving it out means orkhan.art, as before.
 */
export interface RoomOptions {
  /** False on another client's site: only the entry's own template counts. */
  handBuilt?: boolean;
}

export function resolveRoom(entry: RoomEntry | null | undefined, options: RoomOptions = {}): RoomSpec | null {
  if (!entry) return null;
  const handBuiltRooms = options.handBuilt ?? true;
  if (handBuiltRooms && entry.slug === 'circular_repetition') return { kind: 'portal' };

  // The entry's own choice comes first, so a work with a hand-built room can still be re-housed.
  const template = entry.room?.template;
  if (template && TEMPLATES.includes(template)) {
    const film = filmUrl(entry.room?.film) ?? filmUrl(entry.video);
    if (film) return { kind: 'template', template, film, dimensions: entry.dimensions };
  }

  const handBuilt = handBuiltRooms ? HAND_BUILT[entry.slug] : undefined;
  if (handBuilt) return { kind: 'handBuilt', room: handBuilt };

  return null;
}

/** Whether an entry has a room, told from as little as its bare record (ids in place of files). */
export function hasRoom(entry: RoomEntry | null | undefined, options: RoomOptions = {}): boolean {
  if (!entry) return false;
  const handBuiltRooms = options.handBuilt ?? true;
  if (handBuiltRooms && entry.slug === 'circular_repetition') return true;
  if (entry.room?.template && (entry.room.film || entry.video)) return true;
  return handBuiltRooms && Boolean(HAND_BUILT[entry.slug]);
}

/** Where rooms opened before they moved under their entries; entries still link to these. */
const FORMER_ADDRESSES = new Set(['/visions', '/savingaesthetics', '/savingaesthetics/room', '/tisa', '/tisa-2', '/memememory']);

/** Does this link open a room? True for a room's address and for the addresses rooms used to have. */
export function opensRoom(url?: string | null): boolean {
  if (!url) return false;
  // An entry may hold the link as a full address or with a trailing slash; both mean the same room.
  const path = (url.replace(/^https?:\/\/(www\.)?orkhan\.art/, '').split(/[?#]/)[0] || '/').replace(/\/+$/, '') || '/';
  return FORMER_ADDRESSES.has(path) || /^\/catalog\/(installations|immersive)\/[^/]+\/room$/.test(path);
}
